import { memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatAuthorData } from 'utility/functions';
import type { TDashboard } from '../utils/constants';

interface DashboardOwnerFieldProps {
  dashboard: TDashboard;
}

/**
 * Display the owner name for a dashboard
 * - System dashboards show "BLOX"
 * - Custom dashboards show the owner's name fetched by UUID
 */
export const DashboardOwnerField = memo(({ dashboard }: DashboardOwnerFieldProps) => {
  const { t } = useTranslation();
  const dataProvider = useDataProvider();
  const { type = '', owner_id = '' } = dashboard;

  // For custom dashboards, fetch the user data by UUID
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', 'dashboard-owner', owner_id],
    queryFn: async () => {
      if (!owner_id) return null;
      try {
        const result = await dataProvider.getUserAccountById(owner_id);
        return result;
      } catch (error) {
        console.error('Error fetching dashboard owner:', error);
        return null;
      }
    },
    enabled: !!owner_id && type === 'custom',
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });

  // For system dashboards, return "BLOX" immediately
  if (type === 'system') {
    return <Typography variant="body2">{t('blox.general.blox')}</Typography>;
  }

  if (isLoading) {
    return <Typography variant="body2">{t('blox.general.loading')}</Typography>;
  }

  if (!userData) {
    return <Typography variant="body2">{owner_id}</Typography>; // Fallback to UUID
  }

  // Format the user's name using the same helper used for authors
  const displayName = formatAuthorData(userData, { includeEmail: false });

  return (
    <Typography
      variant="body2"
      title={userData?.email || owner_id}
    >
      {displayName || owner_id}
    </Typography>
  );
});

DashboardOwnerField.displayName = 'DashboardOwnerField';

export default DashboardOwnerField;
